
import React, { useEffect, useState, useMemo } from 'react';
import { collection, addDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

const toYMD=(d)=>{ if(!d)return''; if(typeof d==='string'&&/^\d{4}-\d{2}-\d{2}$/.test(d)) return d; const dt=new Date(d); if(isNaN(dt))return''; const y=dt.getFullYear(), m=String(dt.getMonth()+1).padStart(2,'0'), day=String(dt.getDate()).padStart(2,'0'); return `${y}-${m}-${day}`; };
const prettyDate=(ymd)=>!ymd?'':new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined,{weekday:'long',month:'short',day:'numeric',year:'numeric'});
const viewerURL=(n)=>{
  if(n.linkURL) return n.linkURL;
  if(n.downloadURL){
    if(/\.pdf(\?|$)/i.test(n.downloadURL)) return n.downloadURL;
    return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(n.downloadURL)}`;
  }
  return 'about:blank';
};

export default function DailyNotes(){
  const [notes,setNotes]=useState([]);
  const [search,setSearch]=useState('');
  const [day,setDay]=useState(toYMD(new Date()));
  const [title,setTitle]=useState('');
  const [file,setFile]=useState(null);
  const [linkURL,setLinkURL]=useState('');
  const [activeId,setActiveId]=useState(null);
  const [busy,setBusy]=useState(false);
  const [progress,setProgress]=useState(0);
  const [errMsg,setErrMsg]=useState('');

  useEffect(()=>{
    const qy=query(collection(db,'dailyNotes'),orderBy('day','desc'),orderBy('createdAt','desc'));
    return onSnapshot(qy,snap=>{
      const arr=snap.docs.map(d=>({id:d.id,...d.data()}));
      setNotes(arr); if(!activeId&&arr.length) setActiveId(arr[0].id);
    },e=>setErrMsg(`Listen error: ${e.message||e.code}`));
  },[activeId]);

  const filtered=useMemo(()=>{
    const q=search.trim().toLowerCase();
    return notes.filter(n=>!q||(n.title||'').toLowerCase().includes(q)||(n.day||'').includes(q));
  },[notes,search]);

  const active=notes.find(n=>n.id===activeId)||null;

  const reset=()=>{ setTitle(''); setFile(null); setLinkURL(''); setDay(toYMD(new Date())); setProgress(0); setBusy(false); setErrMsg(''); };

  const saveNote=async(e)=>{
    e.preventDefault(); setErrMsg('');
    try{
      if(!title.trim()){ setErrMsg('Title is required.'); return; }
      if(!file && !linkURL.trim()){ setErrMsg('Upload a file OR paste a Google Docs link.'); return; }
      setBusy(true);
      let downloadURL=''; let path='';
      if(file){
        path=`daily-notes/${Date.now()}_${file.name}`;
        const storageRef=ref(storage,path);
        const task=uploadBytesResumable(storageRef,file);
        await new Promise((resolve,reject)=>{
          task.on('state_changed', snap=>setProgress(Math.round((snap.bytesTransferred/snap.totalBytes)*100)), reject, resolve);
        });
        downloadURL=await getDownloadURL(task.snapshot.ref);
      }
      const docRef=await addDoc(collection(db,'dailyNotes'),{
        title:title.trim(),
        day,
        linkURL: linkURL.trim()||'',
        downloadURL,
        storagePath: path || '',
        createdAt: new Date().toISOString()
      });
      setActiveId(docRef.id);
      reset();
    }catch(err){ console.error(err); setErrMsg(err.message||'Error saving note.'); }
    finally{ setBusy(false); }
  };

  const removeNote=async(n)=>{
    if(!window.confirm('Delete this note?')) return;
    try{
      await deleteDoc(doc(db,'dailyNotes',n.id));
      if(n.storagePath) try{ await deleteObject(ref(storage,n.storagePath)); }catch{}
      if(activeId===n.id) setActiveId(null);
    }catch(err){ alert('Error deleting note.'); }
  };

  return (<div style={{display:'grid',gridTemplateColumns:'360px 1fr',gap:'18px'}}>
    <div>
      <div className='card dn-form'>
        <h3 style={{marginTop:0}}>Daily Notes</h3>
        <form onSubmit={saveNote}>
          <label>Title *</label>
          <input value={title} onChange={e=>setTitle(e.target.value)} />
          <label>Day</label>
          <input type='date' value={day} onChange={e=>setDay(e.target.value)} />
          <label>Upload (.pdf / .docx / .txt)</label>
          <input type='file' accept='.pdf,.doc,.docx,.txt,.rtf' onChange={e=>setFile(e.target.files?.[0]||null)} />
          <div style={{textAlign:'center',margin:'8px 0',color:'#6b7280'}}>— or —</div>
          <label>Google Docs Link (optional)</label>
          <input placeholder='https://docs.google.com/document/...' value={linkURL} onChange={e=>setLinkURL(e.target.value)} />
          {errMsg && <div style={{color:'#ef4444',marginTop:6}}>{errMsg}</div>}
          {busy && <div style={{marginTop:6}}>Uploading… {progress}%</div>}
          <div className='form-actions'>
            <button className='btn primary' type='submit' disabled={busy}>Save Note</button>
            <button className='btn' type='button' onClick={reset} disabled={busy}>Reset</button>
          </div>
        </form>
      </div>

      <div className='card'>
        <div className='controls'>
          <input className='search' placeholder='Search title or date (YYYY-MM-DD)' value={search} onChange={e=>setSearch(e.target.value)} />
          <div className='counts'>{filtered.length} note(s)</div>
        </div>
        <div style={{display:'grid',gap:8}}>
          {filtered.map(n=>(
            <div key={n.id} className={`card dn-item ${activeId===n.id?'active':''}`} style={{padding:'10px',cursor:'pointer',border:'1px solid #e5e7eb'}} onClick={()=>setActiveId(n.id)}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontWeight:600}}>{n.title}</div>
                  <div style={{fontSize:12,color:'#6b7280'}}>{prettyDate(n.day)} {n.linkURL?'• link':''} {n.downloadURL?'• file':''}</div>
                </div>
                <div>
                  <button type='button' className='btn tiny linklike' onClick={(e)=>{e.stopPropagation(); removeNote(n);}}>Delete</button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length===0 && <div className='empty'>No notes found.</div>}
        </div>
      </div>
    </div>

    <div className='card' style={{minHeight:'70vh'}}>
      <h3 style={{marginTop:0}}>{active ? active.title : 'Preview'}</h3>
      {active ? (
        active.downloadURL || active.linkURL ? (
          <iframe title='Daily note preview' src={viewerURL(active)} style={{width:'100%', height:'70vh', border:'1px solid #e5e7eb', borderRadius:6}} />
        ) : <div className='empty'>No file or link on this note.</div>
      ) : <div className='empty'>Select a note to preview.</div>}
    </div>
  </div>);
}
